import { useQuery } from '@tanstack/react-query';
import { activityApi } from '../services/adminApi';
import { queryKeys } from '../utils/queryKeys';
import type { ActivityFilters } from '../types/activity';

/**
 * Hook for fetching activities list with React Query
 */
export function useActivities(filters: Partial<ActivityFilters>) {
  return useQuery({
    queryKey: queryKeys.activities.list(filters),
    queryFn: () => activityApi.listActivities(filters),
    placeholderData: (previousData) => previousData, // Keep previous data while loading
    staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
}

/**
 * Hook for fetching available tags
 */
export function useTags() {
  return useQuery({
    queryKey: queryKeys.tags.list(),
    queryFn: () => activityApi.getTags(),
    staleTime: 10 * 60 * 1000, // Tags change rarely, keep fresh for 10 minutes
  });
}

/**
 * Hook for fetching available time period transitions
 */
export function useTransitions() {
  return useQuery({
    queryKey: queryKeys.transitions.list(),
    queryFn: () => activityApi.getTransitions(),
    staleTime: 10 * 60 * 1000, // Transitions are fixed, keep fresh for 10 minutes
  });
}

/**
 * Hook for fetching development area tags for the generation form
 */
export function useDevelopmentAreas() {
  return useQuery({
    queryKey: queryKeys.developmentAreas.list(),
    queryFn: () => activityApi.getDevelopmentAreas(),
    staleTime: 10 * 60 * 1000, // Development areas change rarely, keep fresh for 10 minutes
  });
}

/**
 * Hook for fetching all activity categories
 */
export function useCategories() {
  return useQuery({
    queryKey: queryKeys.categories.list(),
    queryFn: () => activityApi.getCategories(),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Hook for fetching the lightweight activity list for the schedule modal panel.
 */
export function useModalActivities() {
  return useQuery({
    queryKey: queryKeys.activities.modalList(),
    queryFn: () => activityApi.listModalActivities(),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function usePredefinedActivityImages() {
  return useQuery({
    queryKey: queryKeys.predefinedActivityImages.list(),
    queryFn: async () => {
      const response = await activityApi.getPredefinedActivityImages();
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Type guard to check if error is an Error object
 */
export function isError(error: unknown): error is Error {
  return error instanceof Error;
}
